import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import Slider from 'rc-slider';
import styles from './PlayerControls.module.scss';
import {
  DownloadIcon,
  FullscreenIcon,
  MuteIcon,
  PauseIcon,
  PlayButtonIcon,
  VolumeIcon,
  CloseFullScreenIcon,
  NoSubtitlesIcon,
  SubtitlesExistIcon,
  BackwardIcon,
  ForwardIcon,
} from '../Player/VideoControls';
import Duration from '../Player/Duration';
import cx from 'classnames';
import supportedLanguages from '../../../common/constants/supportedLanguages.json';
import VideoSubtitles from '../VideoSubtitles/VideoSubtitles';
import { parseSync } from 'subtitle';
import VideoService from '../../service/VideoService';
import SubtitlesLanguageMenu from '../SubtitlesLanguageMenu/SubtitlesLanguageMenu';
import VideoSpeedMenu from '../VideoSpeedMenu/VideoSpeedMenu';
import { useTranslation } from 'react-i18next';
import Hint from '../Hint/Hint';
import { detectMob } from '../../../engage/utils/isMobile';
import Icon from '../Icon/Icon';
import { useCallToActionContext } from '../../context/CallToActionContext';
import { useChaptersContext } from '../../context/ChaptersContext';
import position from './position.svg';
import { useTrimContext } from '../../context/VideoTrimContex';
import { useZoomContext } from '../../context/ZoomContext';
import useWindowSize from '../../hooks/useWindowSize';
import { useVoiceoverContext } from '../../context/VoiceoverContext';
import PlayerComment from '../PlayerComment/PlayerComment';
import { ENTER_KEY_CODE } from '../../constants/common';
import { useInPlayerCommentContext } from '../../context/InPlayerCommentContext';

const rewindStep = 5; // value is seconds
const fastForwardStep = 5; // value in seconds
const MAX_WIDTH_TITLE = 240;
const DURATION_CONTAINER_TOP_OFFSET = 15;

const PlayerControls = ({
  isReady,
  playerNode,
  playerOnly,
  id,
  seeking,
  onlyPlayControlShow,
  native,
  error,
  showOverlay,
  isMobileView,
  onSeekMouseDown,
  onSeekChange,
  onSeekMouseUp,
  togglePlaying,
  hideControls,
  playing,
  handleChangeVolume,
  toggleMuted,
  volume,
  hideSlider,
  played,
  duration,
  calculatedPlayedValue,
  disableDownload,
  downloadNotification,
  onDownloadVideo,
  toggleFullscreen,
  sliderWidth,
  isFullScreenAllowed,
  isFullScreen,
  currentLanguage,
  availableLanguages,
  onLanguageSelected,
  showSubtitlesExistance,
  isSubtitlesExist,
  subtitles,
  subtitlesSettings,
  customTiming,
  isFirstPlay,
  enableSubtitles,
  showSubtitlesControlExternal,
  isEmbeddedMode,
  hideControlsOnStart,
  playbackRate,
  handleChangePlaybackRate,
  isBackwardAndForwardActionsAllows,
  videoConfig,
  hasSrc = true,
  videoMaxDimensions,
  mixedOrientation,
  blockSimulatedResources,
  processing,
}) => {
  const [showVolumeBar, setShowVolumeBar] = useState(false);
  const [languagesList, setLanguagesList] = useState([]);
  const [currentText, setCurrentText] = useState('');
  const [parsedSubtitles, setParsedSubtitles] = useState();
  const [showDownloadNotification, setShowDownloadNotification] = useState(false);
  const [currentChapter, setCurrentChapter] = useState({ styles: { left: 0 }, title: '' });
  const [enableChapters, setEnableChapters] = useState(true);

  const { isChaptersEditor, chapters } = useChaptersContext();
  const { trimmingOn } = useTrimContext();

  const mediaMatch = window.matchMedia('(max-width: 600px)');
  const isSmallScreenForEmbedded = useMemo(() => mediaMatch.matches, [mediaMatch]);

  const { isCallToActionOpened } = useCallToActionContext();

  const { isVoiceoverOpened, isVoiceoverRecording, isVoiceoverCountdown } = useVoiceoverContext();

  const { isZoomOpened } = useZoomContext();

  const { height } = useWindowSize();

  const { timestampComments } = useInPlayerCommentContext();

  const durationTopPosition = useMemo(() => {
    if (playerNode) {
      const { top = 0 } = playerNode?.getBoundingClientRect();
      return `${(top ?? 0) + DURATION_CONTAINER_TOP_OFFSET}px`;
    }
  }, [playerNode, isReady, height]);

  const currentChapters = useMemo(() => structuredClone(chapters), [chapters]);

  const marksObject = useMemo(() => {
    const showChapters = currentChapters?.length && (isChaptersEditor || enableChapters);

    return showChapters
      ? currentChapters
        .map(item => item.startTime / 1000 / duration)
        ?.reduce((a, v) => ({ ...a, [`${v}`]: ' ' }), { ['-0.0001']: ' ' })
      : {};
  }, [currentChapters, duration, enableChapters, isChaptersEditor]);

  const additionalCheckForEmdeddedMode = useMemo(() => {
    return isEmbeddedMode ? detectMob() || isSmallScreenForEmbedded : isMobileView;
  }, [isEmbeddedMode, detectMob, isSmallScreenForEmbedded, isMobileView]);

  const dotsStyle = useMemo(() => {
    const height = additionalCheckForEmdeddedMode ? 4 : 8;
    const width = additionalCheckForEmdeddedMode ? 6 : 10;
    const halfHeight = `${height / 2}px`;
    const bottom = `${additionalCheckForEmdeddedMode ? 0 : -4}px`;
    return {
      width: `${width}px`,
      height: `${height}px`,
      border: 'none',
      borderRadius: 0,
      bottom,
      background: '#BDBDBD',
      background: `radial-gradient(circle 20px at -20% 50%, transparent, transparent ${halfHeight}, #BDBDBD ${halfHeight}), radial-gradient( circle 20px at 120% 50%, transparent, transparent ${halfHeight}, #BDBDBD ${halfHeight})`,
      backgroundSize: `${width / 2}px ${height}px, ${width / 2}px ${height}px`,
      backgroundPosition: '0 0, 100% 0',
      backgroundRepeat: 'no-repeat',
    };
  }, [additionalCheckForEmdeddedMode]);

  const { t } = useTranslation();

  const sliderRef = useRef(null);

  const handleOnMouseOverVolume = useCallback(() => {
    if (!showVolumeBar) setShowVolumeBar(true);
  }, [showVolumeBar]);

  const handleOnMouseLeaveVolume = useCallback(() => {
    if (showVolumeBar) setShowVolumeBar(false);
  }, [showVolumeBar]);

  const handleLanguageChoose = language => {
    onLanguageSelected(language);
  };

  const handleVideoDownload = useCallback(() => {
    if (!disableDownload) {
      playing && togglePlaying();
      onDownloadVideo();
    }
  }, [disableDownload, togglePlaying, playing, onDownloadVideo]);

  const handleMouseOver = useCallback(() => {
    disableDownload && setShowDownloadNotification(true);
  }, [disableDownload]);

  const handleMouseLeave = useCallback(() => {
    disableDownload && setShowDownloadNotification(false);
  }, [disableDownload]);

  const handleRewindDown = () => {
    onSeekMouseDown();
  };

  const handleFastForwardDown = () => {
    onSeekMouseDown();
  };

  const handleRewindUp = () => {
    const resultValue = calculatedPlayedValue - rewindStep;
    onSeekMouseUp(resultValue < 0 ? 0 : resultValue / duration);
  };

  const handleFastForwardUp = () => {
    const resultValue = calculatedPlayedValue + fastForwardStep;
    onSeekMouseUp(resultValue > duration ? 100 : resultValue / duration);
  };

  const handleRewind = () => {
    const resultValue = calculatedPlayedValue - rewindStep;
    onSeekMouseUp(resultValue < 0 ? 0 : resultValue / duration);
  };

  const handleFastForward = () => {
    const resultValue = calculatedPlayedValue + fastForwardStep;
    onSeekMouseUp(resultValue > duration ? 100 : resultValue / duration);
  };

  const handleMouseOverSlider = useCallback(
    e => {
      if (currentChapters && enableChapters) {
        const sliderDimensions = sliderRef.current.getBoundingClientRect();
        const mousePosition = (e.pageX - sliderDimensions.left) / e.currentTarget.offsetWidth;
        const title = currentChapters.filter(item => mousePosition * duration > item.startTime / 1000).reverse()[0]?.title;
        let left = e.pageX - sliderDimensions.left;
        let transform = 'translateY(-100%)';
        if (left + MAX_WIDTH_TITLE > sliderDimensions.width) {
          transform = `${transform} translateX(-100%)`;
        }
        setCurrentChapter({ title, styles: { left, transform } });
      }
    },
    [sliderRef, duration, enableChapters, currentChapters]
  );

  const toggleChaptersShowing = () => {
    setEnableChapters(prevState => !prevState);
  };

  useEffect(() => {
    availableLanguages && setLanguagesList(availableLanguages?.map(item => supportedLanguages.find(language => language.id === item)));
  }, [availableLanguages]);

  useEffect(() => {
    if (isMobileView) {
      if (seeking.current) {
        const title = chapters.filter(item => played * duration > item.startTime / 1000).reverse()[0]?.title;
        let left = window.screen.width * played;
        let transform = 'translateY(-100%)';
        if (left + MAX_WIDTH_TITLE > window.screen.width) {
          transform = `${transform} translateX(-100%)`;
        }
        setCurrentChapter({ title, styles: { left, transform } });
      }
    }
  }, [seeking, played]);

  useEffect(() => {
    hideControls && setCurrentChapter(null);
  }, [hideControls]);

  const updateSubtitles = async (language, subtitles) => {
    const response = await fetch(subtitles.src);
    const subs = await response.text();
    setParsedSubtitles(prevState => ({ ...prevState, [language]: { ...subtitles, src: parseSync(subs) } }));
  };

  useEffect(() => {
    const generateSubtitles = async () => {
      if (enableSubtitles) {
        console.log('its not iOS, need to parse subs', subtitles);
        // The current language should be loaded first
        if (subtitles[currentLanguage]) {
          await updateSubtitles(currentLanguage, subtitles[currentLanguage]);
        }

        for (const key in subtitles) {
          key !== currentLanguage && (await updateSubtitles(key, subtitles[key]));
        }
      }
    };

    subtitles && generateSubtitles();
  }, [subtitles]);

  useEffect(() => {
    if (parsedSubtitles && enableSubtitles) {
      if (blockSimulatedResources) {
        setCurrentText('');
      } else {
        const currentSubtitle = parsedSubtitles[currentLanguage]?.src?.find(
          item => calculatedPlayedValue * 1000 >= item.data.start && calculatedPlayedValue * 1000 <= item.data.end
        );
        if (currentSubtitle) {
          setCurrentText(currentSubtitle.data.text);
        } else {
          setCurrentText('');
        }
      }
    } else {
      currentText.length > 0 && setCurrentText('');
    }
  }, [calculatedPlayedValue, currentLanguage, blockSimulatedResources]);

  if (onlyPlayControlShow) return null;

  return (
    <>
      <div
        className={cx(styles.controlsBox, {
          [styles.controlsBoxEmbeddedMode]: isEmbeddedMode && !(detectMob() || isSmallScreenForEmbedded),
          [styles.hide]: native || error || showOverlay,
          [styles.show]: (!hideControls || (!playing && isFirstPlay) || !isFirstPlay) && hasSrc && !processing,
          [styles.fadeOut]: hideControls && playing && !isFullScreen && !hideControlsOnStart,
        })}
      >
        {!(hideSlider || trimmingOn || isCallToActionOpened || isVoiceoverOpened) && (
          <div
            className={cx(styles.progressBar, { [styles.mobileFullscreen]: additionalCheckForEmdeddedMode && isFullScreen })}
            onMouseMove={handleMouseOverSlider}
            onMouseLeave={() => setCurrentChapter(null)}
            ref={sliderRef}
          >
            {!blockSimulatedResources && currentChapter?.title && (
              <div className={styles.chapterTitle} style={currentChapter.styles}>
                {currentChapter.title}
              </div>
            )}
            {timestampComments && Object.entries(timestampComments)?.filter(([secondsValue]) => secondsValue <= duration).map(([secondsValue, comments]) => (<PlayerComment
              key={secondsValue}
              id={id}
              secondsValue={secondsValue}
              comments={comments}
              duration={duration}
            />))}
            <Slider
              marks={blockSimulatedResources ? {} : marksObject}
              className={styles.videoProgress}
              trackStyle={{
                backgroundColor: '#7C43AA',
                height: additionalCheckForEmdeddedMode ? '4px' : '8px',
                borderRadius: '0',
              }}
              handleStyle={
                additionalCheckForEmdeddedMode
                  ? {
                    marginTop: '-7px',
                    height: '16px',
                    width: '16px',
                    borderRadius: '50%',
                    backgroundColor: '#7C43AA',
                    zIndex: '101',
                  }
                  : isChaptersEditor
                    ? {
                      backgroundImage: `url(${position})`,
                      backgroundPosition: '50% 0%',
                      borderRadius: '0',
                      border: 'none',
                      boxShadow: 'none',
                      outline: 'none',
                      cursor: 'default',
                      backgroundRepeat: 'no-repeat',
                      backgroundColor: 'transparent',
                      zIndex: '50',
                      height: '16px',
                      top: '3px',
                    }
                    : { display: 'none' }
              }
              railStyle={{
                height: additionalCheckForEmdeddedMode ? '4px' : '8px',
                background: '#F6F6F6',
              }}
              min={sliderWidth.min}
              max={sliderWidth.max}
              step={0.001}
              onBeforeChange={onSeekMouseDown}
              onChange={onSeekChange}
              onAfterChange={onSeekMouseUp}
              value={played}
              dotStyle={dotsStyle}
              included
            />
          </div>
        )}
        <div className={cx(styles.controls, { [styles.mobileFullscreen]: additionalCheckForEmdeddedMode && isFullScreen })}>
          <div className={cx(styles.leftControls, { [styles.noForwardBackwardActions]: !isBackwardAndForwardActionsAllows })}>
            {!isVoiceoverRecording && !isVoiceoverCountdown && (
              <div
                role="button"
                className={cx(styles.control, styles.playButton, {
                  [styles.hide]: additionalCheckForEmdeddedMode && !playing,
                })}
                onClick={togglePlaying}
                onKeyDown={event => {
                  if (event.keyCode === ENTER_KEY_CODE) {
                    togglePlaying();
                  }
                }}
                title={playing ? 'Pause' : 'Play'}
                tabIndex={0}
              >
                {playing ? <PauseIcon /> : <PlayButtonIcon />}
              </div>
            )}
            {isBackwardAndForwardActionsAllows && (
              <>
                <div
                  className={cx(styles.control, styles.backwardButton, { [styles.hide]: additionalCheckForEmdeddedMode && !playing })}
                  onMouseDown={handleRewindDown}
                  onMouseUp={handleRewindUp}
                  onClick={handleRewind}
                >
                  <BackwardIcon />
                </div>
                <div
                  className={cx(styles.control, styles.forwardButton, { [styles.hide]: additionalCheckForEmdeddedMode && !playing })}
                  onMouseDown={handleFastForwardDown}
                  onMouseUp={handleFastForward}
                  onClick={handleFastForwardUp}
                >
                  <ForwardIcon />
                </div>
              </>
            )}
            <div
              className={cx(styles.volumeBarWrapper)}
              onMouseEnter={handleOnMouseOverVolume}
              onMouseLeave={handleOnMouseLeaveVolume}
            >
              <div
                className={cx(styles.control)}
                onClick={toggleMuted}
                role="button"
                tabIndex={0}
                onKeyDown={event => {
                  if (event.keyCode === ENTER_KEY_CODE) toggleMuted();
                }}
                title={volume.current ? t('accessibility.unmute') : t('accessibility.mute')}
              >
                {volume.current ? <VolumeIcon /> : <MuteIcon />}
              </div>
              <Slider
                className={cx(styles.control, styles.volumeBar)}
                min={0}
                max={1}
                step={0.001}
                onChange={value => handleChangeVolume(value)}
                value={volume.current}
                included
                style={{
                  opacity: 1,
                  width: isEmbeddedMode && !(detectMob() || isSmallScreenForEmbedded) ? '70px' : '80px',
                }}
                handleStyle={{
                  width: '18px',
                  height: '18px',
                  marginTop: '-7px',
                  background: '#FFFFFF',
                }}
                trackStyle={{
                  background: '#FFFFFF',
                  height: '4px',
                }}
                railStyle={{
                  background: '#FFFFFF',
                  height: '4px',
                }}
              />
            </div>
          </div>
          <div
            style={{ ...(isMobileView && top ? { top: durationTopPosition } : {}) }}
            className={cx(styles.duration, { [styles.smallPadding]: isSubtitlesExist && chapters?.length > 0 })}
          >
            {Number.isFinite(duration) && !isZoomOpened && (
              <span>
                <Duration seconds={customTiming && !trimmingOn ? customTiming.progress : calculatedPlayedValue} />
                <span className={styles.durationSeparator}>{' / '}</span>
                <Duration seconds={customTiming && !trimmingOn ? customTiming.duration : duration} />
              </span>
            )}
          </div>
          {!blockSimulatedResources && chapters?.length && !isChaptersEditor > 0 ? (
            <div
              role="button"
              className={cx(styles.control, styles.chapters)}
              onClick={toggleChaptersShowing}
              title="chapters"
              tabIndex={0}
            >
              <Icon src={enableChapters ? 'chaptersEnabled' : 'chaptersDisabled'} className={styles.chapterIndicator} />
            </div>
          ) : null}
          {!blockSimulatedResources && ((isSubtitlesExist && onLanguageSelected && !showSubtitlesExistance) ||
            (showSubtitlesControlExternal && availableLanguages && !isVoiceoverOpened)) && (
              <SubtitlesLanguageMenu
                playerNode={playerNode}
                languagesList={languagesList}
                currentLanguage={currentLanguage}
                handleLanguageChoose={handleLanguageChoose}
                hideControls={hideControls && playing}
                isMobileView={isMobileView}
                className={cx(styles.control, styles.subtitles)}
              />
            )}
          {!blockSimulatedResources && showSubtitlesExistance && (
            <div className={styles.existWrapper}>{isSubtitlesExist ? <SubtitlesExistIcon /> : <NoSubtitlesIcon />}</div>
          )}
          {!isVoiceoverOpened && (
            <VideoSpeedMenu
              playerNode={playerNode}
              playbackRate={playbackRate}
              handleChangePlaybackRate={handleChangePlaybackRate}
              hideControls={hideControls && playing}
              isMobileView={isMobileView}
            />
          )}
          {onDownloadVideo && (
            <div
              role="button"
              className={cx(styles.control, styles.download, { [styles.disable]: disableDownload })}
              onClick={handleVideoDownload}
              onMouseOver={handleMouseOver}
              onMouseLeave={handleMouseLeave}
              onKeyDown={event => {
                if (event.keyCode === ENTER_KEY_CODE) {
                  handleVideoDownload();
                }
              }}
              title="download video"
              tabIndex={0}
            >
              <DownloadIcon />
              {showDownloadNotification ? (
                <Hint
                  className={styles.notification}
                  text={downloadNotification || t('my_video.download.not_supported_notification')}
                />
              ) : null}
            </div>
          )}
          {isFullScreenAllowed && (
            <div
              role="button"
              className={cx(styles.control, styles.fullscreen)}
              onClick={toggleFullscreen}
              onKeyDown={event => {
                if (event.keyCode === ENTER_KEY_CODE) {
                  togglePlaying();
                }
              }}
              title="fullscreen video"
              tabIndex={0}
            >
              {isFullScreen ? <CloseFullScreenIcon /> : <FullscreenIcon />}
            </div>
          )}
        </div>
      </div>
      <VideoSubtitles
        playerNode={playerNode}
        subtitlesSettings={subtitlesSettings}
        text={currentText}
        videoControlsVisible={!hideControls || !playing}
        isFullScreen={isFullScreen || playerOnly}
        isMobileView={isMobileView}
        videoMaxDimensions={videoMaxDimensions}
        mixedOrientation={mixedOrientation}
      />
    </>
  );
};

export default PlayerControls;
